/**
 * pilotMode surface fold — tenant settings → visible/hidden route hints (P1b).
 *
 * @see src/shared/platform/pilot-surface-registry.ts
 * @see .cursor/plans/archived/2026-q2/productization-7w/stream-p1-pilot-mode-surface-folding.md
 */

import { useMemo } from 'react';
import {
  isPathHiddenByPilotFold,
  resolvePilotFoldFallbackPath,
  resolvePilotSurfaceGuardState,
  type PilotSurfaceGuardState,
} from '@shared/platform/pilot-surface-guard';
import { parseTenantPilotSettings } from '@shared/types/tenant-pilot-settings-parse';
import { useTenant } from '../../contexts/TenantContext';

export interface PilotSurfaces extends PilotSurfaceGuardState {
  pilotSettings: ReturnType<typeof parseTenantPilotSettings>;
  fallbackPath: string;
  isPathHidden: (pathname: string) => boolean;
}

export function resolvePilotSurfacesFromSettings(
  rawSettings: unknown
): Omit<PilotSurfaces, 'isPathHidden'> {
  const pilotSettings = parseTenantPilotSettings(rawSettings);
  const guard = resolvePilotSurfaceGuardState(pilotSettings);

  return {
    ...guard,
    pilotSettings,
    fallbackPath: resolvePilotFoldFallbackPath(guard),
  };
}

/**
 * Reads `tenants.settings.pilot` via TenantContext (Supabase row settings JSON).
 */
export function usePilotSurfaces(): PilotSurfaces {
  const { currentTenant } = useTenant();

  return useMemo(() => {
    const resolved = resolvePilotSurfacesFromSettings(currentTenant?.settings as unknown);
    return {
      ...resolved,
      isPathHidden: (pathname: string) => isPathHiddenByPilotFold(pathname, resolved),
    };
  }, [currentTenant?.settings, currentTenant?.id]);
}
